import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Checkbox,
  Select,
  MenuItem,
  IconButton,
  Button,
  Dialog,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import CloseIcon from "@material-ui/icons/Close";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { appUrl } from "../../../../../utils/config";
import { useApiBaseUrl } from "../CommonMethods/CommonMethods";

// PO Status / Batch Status seed data now comes from GET /fetchFGStatusesDirect/false
// (same endpoint POStatusTab.jsx / BatchStatusTab.jsx already use — it returns both
// poStatuses and batchStatuses together). Changed Field seed data now comes from
// GET /fetchDataElements (same endpoint UpdateCmoConfiguration.jsx already uses).
// Conditions field/operator lists still have no dedicated seed-data endpoint, so they
// stay static until one is provided.
const CONDITIONS_FIELD_OPTIONS = ["Price per unit", "collaboratorTypeID"];
const CONDITIONS_OPERATOR_OPTIONS = ["==", ">", "<", ">=", "<=", "!="];
const SELECT_DAYS_OPTIONS = ["All", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const SELECT_MONTHS_OPTIONS = ["All", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAY_OF_MONTH_OPTIONS = Array.from({ length: 30 }, (_, i) => String(i + 1));
const TIME_ZONE_OPTIONS = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "Asia/Kolkata",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Australia/Sydney",
];

const ALL_OPTION = "All";

// collaboratorTypeID on /fetchChangeFlags rows: the same flag (e.g. changeInQty) exists
// once per collaborator type, so the label carries the type to tell the variants apart.
const COLLABORATOR_TYPE_LABELS = { 1: "Sponsor", 2: "CMO" };

// "All" is checked whenever every individual option (everything but "All" itself) is
// selected — selected never literally contains the string "All", it's derived.
const isAllOptionSelected = (options, selected) => {
  const individual = options.filter((o) => o !== ALL_OPTION);
  return individual.length > 0 && individual.every((o) => selected.includes(o));
};

// MUI's Select always reports "All" as freshly added in e.target.value (since our
// controlled value never literally contains it, MUI can't see it as "already selected"
// to toggle off) — so whether clicking "All" should select-all or clear-all is decided
// by whether it was VISUALLY checked before (i.e. every individual option was already
// selected), not by the raw toggle MUI computed.
const applyAllAwareSelection = (options, previousSelected, rawNextValue) => {
  const individual = options.filter((o) => o !== ALL_OPTION);
  if (rawNextValue.includes(ALL_OPTION)) {
    const wasAllChecked = isAllOptionSelected(options, previousSelected);
    return wasAllChecked ? [] : individual;
  }
  return rawNextValue.filter((o) => o !== ALL_OPTION);
};

let nextConditionRowId = 1;
const newConditionRowId = () => nextConditionRowId++;

const isCompleteCondition = (c) => !!c.field && !!c.operator && c.value !== "" && c.value != null;

// A Change Flag trigger's triggerPointValue is the bare collabrixChangeFlagID, or, when
// the Conditions grid has rows, JSON {"value": "<flagId>", "conditions": [...]} — the
// format the middleware trigger evaluator reads. The grid applies to every selected flag.
const encodeChangeFlagValue = (flagId, conditions) => {
  const complete = conditions.filter(isCompleteCondition);
  if (complete.length === 0) return flagId;
  return JSON.stringify({
    value: flagId,
    conditions: complete.map(({ field, operator, value }) => ({ field, operator, value: String(value) })),
  });
};

const decodeChangeFlagValue = (raw) => {
  const text = raw == null ? "" : String(raw).trim();
  if (!text.startsWith("{")) return { value: text, conditions: [] };
  try {
    const parsed = JSON.parse(text);
    return { value: String(parsed.value ?? ""), conditions: Array.isArray(parsed.conditions) ? parsed.conditions : [] };
  } catch (e) {
    return { value: text, conditions: [] };
  }
};

const selectInputProps = { style: { padding: "14px 16px", fontSize: "15px" } };
const compactSelectInputProps = {
  style: { padding: "9px 10px", fontSize: "13px", width: "100%", boxSizing: "border-box" },
};

// The Time field is a plain "HH:MM" from <input type="time">, but the backend expects
// a full "YYYY-MM-DDTHH:mm:ss" timestamp — stamp it with today's date since Day Wise /
// Month Wise triggers are recurring schedules with no calendar date of their own.
const buildTimestamp = (time) => {
  if (!time) return null;
  const [hours, minutes] = time.split(":");
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(hours)}:${pad(minutes)}:00`;
};

// Reverse of buildTimestamp — pulls "HH:MM" back out of a fetched "...THH:mm:ss"
// timestamp so it can prefill the native <input type="time">, which only accepts HH:MM.
const extractTimeOfDay = (timestamp) => {
  if (!timestamp) return "";
  const match = /T(\d{2}:\d{2})/.exec(timestamp);
  return match ? match[1] : "";
};

// Identifies a row independent of collabrixMessageTriggerID, so a freshly-fetched
// "original" row and a freshly-built "current selection" row can be recognized as the
// same logical row (same trigger point + value/day/month) even before either has an ID.
const getTriggerRowKey = (row) =>
  `${row.collabrixTriggerPointID}::${row.triggerPointValue ?? ""}::${row.day ?? ""}::${row.month ?? ""}`;

// Builds messageTriggerList per the /updateMessageTrigger create-or-update contract:
// one element per selected trigger point value; a value that already existed (matched
// against originalDetails, the raw rows fetched via /fetchMessageTrigger) carries its
// collabrixMessageTriggerID forward as an update, a brand-new value gets ID 0 (create),
// and any originally-saved value that's no longer selected (or whose trigger point type
// changed entirely) is sent back with markForDelete: true so the backend removes it.
const buildMessageTriggerList = (draft, collabrixMessageID, collabrixTriggerPointID, originalDetails) => {
  const base = {
    collabrixMessageID,
    collabrixTriggerPointID,
    triggerPointValue: null,
    day: null,
    timestamp: null,
    timeZone: null,
    month: null,
  };

  let current = [];
  switch (draft.triggerPoint) {
    case "PO Status":
      current = draft.poStatuses.map((value) => ({ ...base, triggerPointValue: value }));
      break;
    case "Batch Status":
      current = draft.batchStatuses.map((value) => ({ ...base, triggerPointValue: value }));
      break;
    case "Changed Field":
      current = draft.changedFields.map((value) => ({ ...base, triggerPointValue: value }));
      break;
    case "Event":
      // Backend event names (not eventTypeIDs, which differ per node).
      current = draft.events.map((value) => ({ ...base, triggerPointValue: value }));
      break;
    case "Change Flag":
      // draft.changeFlags holds collabrixChangeFlagIDs (as strings), so the Sponsor and
      // CMO variants of the same flag name are saved as distinct trigger point values.
      current = draft.changeFlags.map((value) => ({
        ...base,
        triggerPointValue: encodeChangeFlagValue(value, draft.conditions),
      }));
      break;
    case "Day Wise":
      current = draft.selectDays.map((day) => ({
        ...base,
        day,
        timestamp: buildTimestamp(draft.time),
        timeZone: draft.timeZone,
      }));
      break;
    case "Month Wise":
      current = draft.selectMonths.map((month) => ({
        ...base,
        day: draft.dayOfMonth,
        timestamp: buildTimestamp(draft.time),
        timeZone: draft.timeZone,
        month,
      }));
      break;
    default:
      current = [];
  }

  const originalByKey = new Map((originalDetails || []).map((row) => [getTriggerRowKey(row), row]));
  const matchedKeys = new Set();

  const upserts = current.map((row) => {
    const key = getTriggerRowKey(row);
    const original = originalByKey.get(key);
    if (original) matchedKeys.add(key);
    return {
      collabrixMessageTriggerID: original?.collabrixMessageTriggerID ?? 0,
      collabrixMessageID,
      collabrixTriggerPointID,
      triggerPointValue: row.triggerPointValue,
      day: row.day,
      timestamp: row.timestamp,
      timeZone: row.timeZone,
      month: row.month,
      activated: true,
      markForDelete: false,
    };
  });

  // Only one trigger point type may be active for a message at a time: any previously
  // saved row that isn't part of the type being saved right now — whether it's a stale
  // value from THIS type (deselected) or every row from a DIFFERENT type entirely (e.g.
  // an existing Batch Status trigger while the user is now saving PO Status) — goes back
  // with markForDelete: true so the backend deactivates it.
  const deletions = (originalDetails || [])
    .filter((row) => !matchedKeys.has(getTriggerRowKey(row)))
    .map((row) => ({
      collabrixMessageTriggerID: row.collabrixMessageTriggerID,
      collabrixMessageID,
      collabrixTriggerPointID: row.collabrixTriggerPointID,
      triggerPointValue: row.triggerPointValue,
      day: row.day,
      timestamp: row.timestamp,
      timeZone: row.timeZone,
      month: row.month,
      activated: false,
      markForDelete: true,
    }));

  return [...upserts, ...deletions];
};

export const defaultTriggerConfig = () => ({
  triggerPoint: "PO Status",
  poStatuses: [],
  batchStatuses: [],
  changedFields: [],
  events: [],
  changeFlags: [],
  conditions: [],
  selectDays: [],
  time: "",
  timeZone: "",
  selectMonths: [],
  dayOfMonth: "",
});

// A message can have multiple, independent trigger point types already saved (e.g. a
// PO Status trigger AND a separate Batch Status trigger). This builds the draft for
// whichever type is selected — used both for the initial prefill on open AND every time
// the Trigger Point dropdown changes, so switching to any already-configured type shows
// its saved values instead of a blank form. A type with no matching rows in
// originalDetails (brand new selection) correctly falls back to the empty defaults.
const buildDraftFromDetails = (triggerPointLabel, collabrixTriggerPointID, originalDetails) => {
  const matching = (originalDetails || []).filter((d) => d.collabrixTriggerPointID === collabrixTriggerPointID);
  const next = { ...defaultTriggerConfig(), triggerPoint: triggerPointLabel };
  switch (triggerPointLabel) {
    case "PO Status":
      next.poStatuses = matching.map((d) => d.triggerPointValue).filter(Boolean);
      break;
    case "Batch Status":
      next.batchStatuses = matching.map((d) => d.triggerPointValue).filter(Boolean);
      break;
    case "Changed Field":
      next.changedFields = matching.map((d) => d.triggerPointValue).filter(Boolean);
      break;
    case "Event":
      next.events = matching.map((d) => d.triggerPointValue).filter(Boolean);
      break;
    case "Change Flag": {
      const decoded = matching.map((d) => decodeChangeFlagValue(d.triggerPointValue));
      next.changeFlags = decoded.map((d) => d.value).filter(Boolean);
      // Saved together from one grid, so every row carries the same conditions.
      next.conditions = (decoded[0]?.conditions || []).map((c) => ({
        rowId: newConditionRowId(),
        field: c.field || "",
        operator: c.operator || "",
        value: c.value ?? "",
      }));
      break;
    }
    case "Day Wise":
      next.selectDays = matching.map((d) => d.day).filter(Boolean);
      next.time = extractTimeOfDay(matching[0]?.timestamp);
      next.timeZone = matching[0]?.timeZone || "";
      break;
    case "Month Wise":
      next.selectMonths = matching.map((d) => d.month).filter(Boolean);
      next.dayOfMonth = matching[0]?.day || "";
      next.time = extractTimeOfDay(matching[0]?.timestamp);
      next.timeZone = matching[0]?.timeZone || "";
      break;
    default:
      break;
  }
  return next;
};

export const canSaveTriggerConfig = (draft) => {
  if (!draft || !draft.triggerPoint) return false;
  switch (draft.triggerPoint) {
    case "PO Status":
      return draft.poStatuses.length > 0;
    case "Batch Status":
      return draft.batchStatuses.length > 0;
    case "Changed Field":
      return draft.changedFields.length > 0;
    case "Event":
      return draft.events.length > 0;
    case "Change Flag":
      return draft.changeFlags.length > 0 && draft.conditions.every(isCompleteCondition);
    case "Day Wise":
      return draft.selectDays.length > 0 && !!draft.time && !!draft.timeZone;
    case "Month Wise":
      return draft.selectMonths.length > 0 && !!draft.dayOfMonth && !!draft.time && !!draft.timeZone;
    default:
      return false;
  }
};

const useStyles = makeStyles((theme) => ({
  modalRoot: {
    fontFamily: theme.typography.fontFamily,
    boxSizing: "border-box",
    overflowX: "hidden",
  },
  cancelRow: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "4px",
    color: "#e53935",
    fontWeight: 600,
    cursor: "pointer",
    fontSize: "15px",
    width: "100%",
    marginBottom: "-8px",
  },
  modalTitle: {
    fontSize: "26px",
    fontWeight: 700,
    color: "#000",
    width: "100%",
  },
  modalBody: {
    padding: theme.spacing(4),
    boxSizing: "border-box",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(3),
  },
  modalInfoBox: {
    display: "flex",
    gap: theme.spacing(6),
    backgroundColor: "#eaf2fc",
    borderRadius: "10px",
    padding: theme.spacing(2.5, 3),
    width: "100%",
    boxSizing: "border-box",
  },
  infoLabel: {
    fontSize: "12px",
    fontWeight: 700,
    color: "#8a94a6",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    marginBottom: "4px",
  },
  infoValue: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#1a1a1a",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  formField: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    boxSizing: "border-box",
  },
  conditionsSection: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    boxSizing: "border-box",
    overflowX: "auto",
  },
  fieldLabel: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#000",
  },
  requiredStar: {
    color: "#e53935",
  },
  selectField: {
    width: "100%",
    borderRadius: "8px",
    backgroundColor: "#fff",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#c9ced6",
      borderWidth: "1px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#f26522",
      borderWidth: "1px",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#f26522",
      borderWidth: "1px",
    },
    "& .MuiSelect-select": {
      backgroundColor: "transparent",
    },
    "& .MuiSelect-select:focus": {
      backgroundColor: "transparent",
    },
  },
  timeInput: {
    border: "1px solid #c9ced6",
    borderRadius: "8px",
    padding: "13px 16px",
    fontSize: "15px",
    width: "220px",
  },
  // Plain native input — no MUI FormControl/OutlinedInput wrapper layers to fight for width.
  // Padding is a touch taller than the Select's own inner padding since MUI renders the
  // Select's visible box from its own default theme padding (inputProps only reaches its
  // hidden native input, not the visible dropdown box), so matching the number literally
  // still rendered shorter — sized by eye against the Field/Operator boxes instead.
  valueInput: {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #c9ced6",
    borderRadius: "8px",
    padding: "16px 10px",
    fontSize: "14px",
    fontFamily: "inherit",
    color: "#000",
    backgroundColor: "#fff",
    outline: "none",
    "&:hover": {
      borderColor: "#f26522",
    },
    "&:focus": {
      borderColor: "#f26522",
    },
  },
  tableHeaderCell: {
    backgroundColor: "#f5f5f5",
    color: "#333",
    fontWeight: 600,
    fontSize: "12px",
    padding: "8px 10px",
    whiteSpace: "nowrap",
  },
  tableBodyCell: {
    fontSize: "12px",
    padding: "8px 10px",
    verticalAlign: "top",
  },
  compactSelect: {
    width: "100%",
    boxSizing: "border-box",
    fontSize: "12px",
    backgroundColor: "#fff",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#c9ced6",
      borderWidth: "1px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#f26522",
      borderWidth: "1px",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#f26522",
      borderWidth: "1px",
    },
    "& .MuiSelect-select": {
      backgroundColor: "transparent",
    },
    "& .MuiSelect-select:focus": {
      backgroundColor: "transparent",
    },
  },
  outlinedAddBtn: {
    border: "1px solid #f26522",
    color: "#f26522",
    borderRadius: "20px",
    padding: "6px 18px",
    textTransform: "none",
    fontWeight: 600,
    marginTop: theme.spacing(1),
    "&:hover": {
      backgroundColor: "#fdeee3",
    },
  },
  deleteIconRed: {
    color: "#e53935",
  },
  modalSaveBtn: {
    backgroundColor: "#f26522",
    color: "#fff",
    borderRadius: "24px",
    padding: "10px 32px",
    fontSize: "15px",
    textTransform: "none",
    fontWeight: 600,
    alignSelf: "flex-start",
    marginTop: theme.spacing(1),
    "&:hover": {
      backgroundColor: "#d4551a",
    },
  },
}));

// Reusable "Configure IDoc Trigger Point" popup for outbound Module Messages rows.
// Trigger Point types and Change Flags are seed data fetched from the backend
// (GET /fetchAllTriggerPoints, GET /fetchChangeFlags). Previously saved configuration
// for this message is fetched live via collabrixMessageID (GET /fetchMessageTrigger/{id})
// rather than passed in by the caller — trigger point config only applies at sponsor level.
const ConfigureIDocTriggerPointDialog = ({ open, onClose, onSave, messageInfo, collabrixMessageID, cmoName, productType }) => {
  const classes = useStyles();
  const API_BASE_URL = useApiBaseUrl(appUrl);
  const [draft, setDraft] = useState(defaultTriggerConfig());
  const [triggerPoints, setTriggerPoints] = useState([]); // [{ collabrixTriggerPointID, triggerPoint }]
  const [changeFlags, setChangeFlags] = useState([]); // [{ collabrixChangeFlagID, changeFlagName, changeFlagFieldName, collaboratorTypeID }]
  const [poStatusOptions, setPoStatusOptions] = useState([]); // active PO statuses [{ id, name }]
  const [batchStatusOptions, setBatchStatusOptions] = useState([]); // active batch status names
  const [dataElementOptions, setDataElementOptions] = useState([]); // data element labels for Changed Field
  const [eventTypeOptions, setEventTypeOptions] = useState([]); // [{ eventType, eventDescription }] for Event
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [originalDetails, setOriginalDetails] = useState([]); // raw rows from /fetchMessageTrigger, kept for create-or-update diffing on save
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    if (open) setSaveError("");
  }, [open]);

  // Seed data: trigger point types, change flags, FG (PO + batch) statuses, data elements, event types
  useEffect(() => {
    if (!open || !API_BASE_URL) return;
    let cancelled = false;
    setLoadingOptions(true);
    Promise.all([
      axios.get(`${API_BASE_URL}/fetchAllTriggerPoints`),
      axios.get(`${API_BASE_URL}/fetchChangeFlags`),
      axios.get(`${API_BASE_URL}/fetchFGStatusesDirect/false`),
      axios.get(`${API_BASE_URL}/fetchDataElements`),
      axios.get(`${API_BASE_URL}/fetchTriggerEventTypes`).catch((err) => {
        console.error("Failed to load event types for the Event trigger point:", err);
        return { data: {} };
      }),
    ])
      .then(([triggerPointsRes, changeFlagsRes, fgStatusesRes, dataElementsRes, eventTypesRes]) => {
        if (cancelled) return;
        setTriggerPoints(triggerPointsRes.data?.triggerPointsList || []);
        setChangeFlags(changeFlagsRes.data?.changeFlagsList || []);

        const fgStatusesOk = fgStatusesRes.data?.status === true || fgStatusesRes.data?.status === "true";
        const fgData = fgStatusesOk ? fgStatusesRes.data?.data : null;
        // PO Status values are saved as status_id: names differ between status lists
        // (22 is "Shared with CMO" in one, "New PO" in another), IDs do not.
        setPoStatusOptions(
          (fgData?.poStatuses || [])
            .filter((s) => s.activated && s.status_name)
            .map((s) => ({ id: String(s.status_id), name: s.status_name }))
        );
        setBatchStatusOptions(
          (fgData?.batchStatuses || []).filter((s) => s.activated).map((s) => s.status_name).filter(Boolean)
        );

        const dataElementsOk = dataElementsRes.data?.status === 200;
        const dataElementsList = dataElementsOk ? dataElementsRes.data?.data?.dataElementsList : null;
        setDataElementOptions(
          (dataElementsList || []).filter((el) => !el.markForDelete).map((el) => el.dataElementLabel).filter(Boolean)
        );
        setEventTypeOptions(eventTypesRes.data?.eventTypesList || []);
      })
      .catch((err) => {
        console.error("Failed to load trigger point popup seed data:", err);
      })
      .finally(() => {
        if (!cancelled) setLoadingOptions(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, API_BASE_URL]);

  // Existing configuration for this message — prefills the form when re-opening an
  // already-configured row (slide 13). Waits for triggerPoints so the saved
  // collabrixTriggerPointID can be resolved back to its label (drives which case renders).
  useEffect(() => {
    if (!open || !API_BASE_URL) return;
    if (!collabrixMessageID) {
      setDraft(defaultTriggerConfig());
      setOriginalDetails([]);
      return;
    }
    if (triggerPoints.length === 0) return;

    let cancelled = false;
    setLoadingExisting(true);
    axios
      .get(`${API_BASE_URL}/fetchMessageTrigger/${collabrixMessageID}`)
      .then((res) => {
        if (cancelled) return;
        const details = res.data?.messageTriggerDetails || [];
        setOriginalDetails(details);
        if (details.length === 0) {
          setDraft(defaultTriggerConfig());
          return;
        }
        // This dialog's single dropdown can only show one trigger point type at a time,
        // and always defaults to "PO Status" on open regardless of which other types
        // this message already has saved (using PO Status's own saved values if any
        // exist, else blank). Switching the dropdown to a different already-configured
        // type re-derives from originalDetails too — see the Select's onChange below.
        const poStatusTypeID = triggerPoints.find((tp) => tp.triggerPoint === "PO Status")?.collabrixTriggerPointID;
        setDraft(buildDraftFromDetails("PO Status", poStatusTypeID, details));
      })
      .catch((err) => {
        console.error("Failed to load existing message trigger config:", err);
        setDraft(defaultTriggerConfig());
        setOriginalDetails([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingExisting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, API_BASE_URL, collabrixMessageID, triggerPoints]);

  const updateDraft = (fields) => setDraft((prev) => ({ ...prev, ...fields }));

  // Change Flag values are collabrixChangeFlagIDs; a saved value that matches no current
  // flag (e.g. an older name-based value) is shown as-is so it can still be removed.
  const getChangeFlagLabel = (value) => {
    const cf = changeFlags.find((f) => String(f.collabrixChangeFlagID) === String(value));
    if (!cf) return String(value);
    const collaboratorType = COLLABORATOR_TYPE_LABELS[cf.collaboratorTypeID];
    return collaboratorType ? `${cf.changeFlagName} (${collaboratorType})` : cf.changeFlagName;
  };

  // A saved value that matches no current status (e.g. an older name-based value) is
  // shown as-is so it can still be removed.
  const getPoStatusLabel = (value) => poStatusOptions.find((o) => o.id === String(value))?.name || String(value);

  // A saved event name no longer in the catalog is still shown so it can be removed.
  const getEventLabel = (value) => {
    const et = eventTypeOptions.find((e) => e.eventType === value);
    return et?.eventDescription ? `${et.eventType} — ${et.eventDescription}` : String(value);
  };

  const addConditionRow = () =>
    setDraft((prev) => ({
      ...prev,
      conditions: [...prev.conditions, { rowId: newConditionRowId(), field: "", operator: "", value: "" }],
    }));
  const updateConditionRow = (rowId, field, value) =>
    setDraft((prev) => ({
      ...prev,
      conditions: prev.conditions.map((c) => (c.rowId === rowId ? { ...c, [field]: value } : c)),
    }));
  const deleteConditionRow = (rowId) =>
    setDraft((prev) => ({ ...prev, conditions: prev.conditions.filter((c) => c.rowId !== rowId) }));

  const handleSave = async () => {
    if (!canSaveTriggerConfig(draft) || saving) return;
    if (!collabrixMessageID) {
      setSaveError("This message hasn't been saved yet — save its Build/Version row before configuring a trigger point.");
      return;
    }
    if (!API_BASE_URL) return;
    const collabrixTriggerPointID =
      triggerPoints.find((tp) => tp.triggerPoint === draft.triggerPoint)?.collabrixTriggerPointID ?? null;
    if (!collabrixTriggerPointID) {
      setSaveError("Could not resolve the selected trigger point. Please reselect it and try again.");
      return;
    }

    const messageTriggerList = buildMessageTriggerList(draft, collabrixMessageID, collabrixTriggerPointID, originalDetails);
    const payload = { messageTriggerList };

    setSaving(true);
    setSaveError("");
    try {
      const res = await axios.post(`${API_BASE_URL}/updateMessageTrigger`, payload);
      const ok = res.data?.status === true || res.data?.status === "true";
      if (!ok) {
        setSaveError(res.data?.message || "Failed to save the trigger point configuration.");
        return;
      }
      const selectedChangeFlags =
        draft.triggerPoint === "Change Flag"
          ? changeFlags.filter((cf) => draft.changeFlags.includes(String(cf.collabrixChangeFlagID)))
          : [];
      onSave({
        ...draft,
        collabrixMessageID,
        collabrixTriggerPointID,
        selectedChangeFlags,
        messageTriggerList,
      });
    } catch (err) {
      console.error("Failed to save trigger point configuration:", err);
      setSaveError(err.response?.data?.message || "An error occurred while saving. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const renderConditionalFields = () => {
    switch (draft.triggerPoint) {
      case "PO Status":
        return (
          <div className={classes.formField}>
            <div className={classes.fieldLabel}>
              PO Status<span className={classes.requiredStar}>*</span>
            </div>
            <Select
              multiple
              displayEmpty
              variant="outlined"
              className={classes.selectField}
              inputProps={selectInputProps}
              disabled={loadingOptions}
              value={draft.poStatuses}
              onChange={(e) => updateDraft({ poStatuses: e.target.value })}
              renderValue={(selected) =>
                selected.length ? selected.map(getPoStatusLabel).join(", ") : "--Select PO Status--"
              }
            >
              {poStatusOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  <Checkbox checked={draft.poStatuses.includes(opt.id)} size="small" />
                  {opt.name}
                </MenuItem>
              ))}
            </Select>
          </div>
        );
      case "Batch Status":
        return (
          <div className={classes.formField}>
            <div className={classes.fieldLabel}>
              Batch Status<span className={classes.requiredStar}>*</span>
            </div>
            <Select
              multiple
              displayEmpty
              variant="outlined"
              className={classes.selectField}
              inputProps={selectInputProps}
              disabled={loadingOptions}
              value={draft.batchStatuses}
              onChange={(e) => updateDraft({ batchStatuses: e.target.value })}
              renderValue={(selected) => (selected.length ? selected.join(", ") : "--Select Batch Status--")}
            >
              {batchStatusOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  <Checkbox checked={draft.batchStatuses.includes(opt)} size="small" />
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </div>
        );
      case "Changed Field":
        return (
          <div className={classes.formField}>
            <div className={classes.fieldLabel}>
              Changed Field<span className={classes.requiredStar}>*</span>
            </div>
            <Autocomplete
              multiple
              loading={loadingOptions}
              options={dataElementOptions}
              value={draft.changedFields}
              onChange={(e, val) => updateDraft({ changedFields: val })}
              renderInput={(params) => <TextField {...params} variant="outlined" placeholder="--Select Changed Field--" />}
            />
          </div>
        );
      case "Event":
        return (
          <div className={classes.formField}>
            <div className={classes.fieldLabel}>
              Event<span className={classes.requiredStar}>*</span>
            </div>
            <Autocomplete
              multiple
              loading={loadingOptions}
              options={eventTypeOptions.map((e) => e.eventType)}
              getOptionLabel={getEventLabel}
              value={draft.events}
              onChange={(e, val) => updateDraft({ events: val })}
              renderInput={(params) => <TextField {...params} variant="outlined" placeholder="--Select Event--" />}
            />
          </div>
        );
      case "Change Flag":
        return (
          <>
            <div className={classes.formField}>
              <div className={classes.fieldLabel}>
                Change Flag<span className={classes.requiredStar}>*</span>
              </div>
              <Autocomplete
                multiple
                loading={loadingOptions}
                options={changeFlags.map((cf) => String(cf.collabrixChangeFlagID))}
                getOptionLabel={getChangeFlagLabel}
                value={draft.changeFlags}
                onChange={(e, val) => updateDraft({ changeFlags: val })}
                renderInput={(params) => <TextField {...params} variant="outlined" placeholder="--Select Change Flag--" />}
              />
            </div>
            <div className={classes.conditionsSection}>
              <div className={classes.fieldLabel}>Conditions</div>
              <Table
                style={{ tableLayout: "fixed", width: "100%", minWidth: "500px", border: "1px solid #ddd" }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeaderCell} style={{ width: "32%" }}>Field</TableCell>
                    <TableCell className={classes.tableHeaderCell} style={{ width: "28%" }}>Operator</TableCell>
                    <TableCell className={classes.tableHeaderCell} style={{ width: "28%" }}>Value</TableCell>
                    <TableCell className={classes.tableHeaderCell} style={{ width: "12%" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {draft.conditions.map((c) => (
                    <TableRow key={c.rowId}>
                      <TableCell className={classes.tableBodyCell} style={{ width: "32%", boxSizing: "border-box" }}>
                        <Select
                          variant="outlined"
                          fullWidth
                          style={{ width: "100%" }}
                          className={classes.compactSelect}
                          inputProps={compactSelectInputProps}
                          displayEmpty
                          value={c.field}
                          onChange={(e) => updateConditionRow(c.rowId, "field", e.target.value)}
                        >
                          <MenuItem value="" disabled>
                            Select Field
                          </MenuItem>
                          {CONDITIONS_FIELD_OPTIONS.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell className={classes.tableBodyCell} style={{ width: "28%", boxSizing: "border-box" }}>
                        <Select
                          variant="outlined"
                          fullWidth
                          style={{ width: "100%" }}
                          className={classes.compactSelect}
                          inputProps={compactSelectInputProps}
                          displayEmpty
                          value={c.operator}
                          onChange={(e) => updateConditionRow(c.rowId, "operator", e.target.value)}
                        >
                          <MenuItem value="" disabled>
                            Select Operator
                          </MenuItem>
                          {CONDITIONS_OPERATOR_OPTIONS.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell className={classes.tableBodyCell} style={{ width: "28%", boxSizing: "border-box" }}>
                        <input
                          type="number"
                          value={c.value}
                          onChange={(e) => updateConditionRow(c.rowId, "value", e.target.value)}
                          className={classes.valueInput}
                        />
                      </TableCell>
                      <TableCell className={classes.tableBodyCell} style={{ width: "12%", boxSizing: "border-box" }}>
                        <IconButton onClick={() => deleteConditionRow(c.rowId)}>
                          <DeleteIcon fontSize="small" className={classes.deleteIconRed} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button className={classes.outlinedAddBtn} onClick={addConditionRow}>
                + Add New Row
              </Button>
            </div>
          </>
        );
      case "Day Wise":
        return (
          <>
            <div className={classes.formField}>
              <div className={classes.fieldLabel}>
                Select Days<span className={classes.requiredStar}>*</span>
              </div>
              <Select
                multiple
                displayEmpty
                variant="outlined"
                className={classes.selectField}
                inputProps={selectInputProps}
                value={draft.selectDays}
                onChange={(e) =>
                  updateDraft({ selectDays: applyAllAwareSelection(SELECT_DAYS_OPTIONS, draft.selectDays, e.target.value) })
                }
                renderValue={(selected) =>
                  !selected.length ? "--Select Days--" : isAllOptionSelected(SELECT_DAYS_OPTIONS, selected) ? "All" : selected.join(", ")
                }
              >
                {SELECT_DAYS_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    <Checkbox
                      checked={opt === ALL_OPTION ? isAllOptionSelected(SELECT_DAYS_OPTIONS, draft.selectDays) : draft.selectDays.includes(opt)}
                      size="small"
                    />
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div className={classes.formField}>
              <div className={classes.fieldLabel}>
                Time<span className={classes.requiredStar}>*</span>
              </div>
              <input
                type="time"
                className={classes.timeInput}
                value={draft.time}
                onChange={(e) => updateDraft({ time: e.target.value })}
              />
            </div>
            <div className={classes.formField}>
              <div className={classes.fieldLabel}>
                Time Zone<span className={classes.requiredStar}>*</span>
              </div>
              <Select
                displayEmpty
                variant="outlined"
                className={classes.selectField}
                inputProps={selectInputProps}
                value={draft.timeZone}
                onChange={(e) => updateDraft({ timeZone: e.target.value })}
              >
                <MenuItem value="" disabled>
                  --Select Time Zone--
                </MenuItem>
                {TIME_ZONE_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </>
        );
      case "Month Wise":
        return (
          <>
            <div className={classes.formField}>
              <div className={classes.fieldLabel}>
                Select Months<span className={classes.requiredStar}>*</span>
              </div>
              <Select
                multiple
                displayEmpty
                variant="outlined"
                className={classes.selectField}
                inputProps={selectInputProps}
                value={draft.selectMonths}
                onChange={(e) =>
                  updateDraft({
                    selectMonths: applyAllAwareSelection(SELECT_MONTHS_OPTIONS, draft.selectMonths, e.target.value),
                  })
                }
                renderValue={(selected) =>
                  !selected.length
                    ? "--Select Months--"
                    : isAllOptionSelected(SELECT_MONTHS_OPTIONS, selected)
                    ? "All"
                    : selected.join(", ")
                }
              >
                {SELECT_MONTHS_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    <Checkbox
                      checked={
                        opt === ALL_OPTION
                          ? isAllOptionSelected(SELECT_MONTHS_OPTIONS, draft.selectMonths)
                          : draft.selectMonths.includes(opt)
                      }
                      size="small"
                    />
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div className={classes.formField}>
              <div className={classes.fieldLabel}>
                Day of Month<span className={classes.requiredStar}>*</span>
              </div>
              <Select
                displayEmpty
                variant="outlined"
                className={classes.selectField}
                inputProps={selectInputProps}
                value={draft.dayOfMonth}
                onChange={(e) => updateDraft({ dayOfMonth: e.target.value })}
              >
                <MenuItem value="" disabled>
                  --Select Day--
                </MenuItem>
                {DAY_OF_MONTH_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div className={classes.formField}>
              <div className={classes.fieldLabel}>
                Time<span className={classes.requiredStar}>*</span>
              </div>
              <input
                type="time"
                className={classes.timeInput}
                value={draft.time}
                onChange={(e) => updateDraft({ time: e.target.value })}
              />
            </div>
            <div className={classes.formField}>
              <div className={classes.fieldLabel}>
                Time Zone<span className={classes.requiredStar}>*</span>
              </div>
              <Select
                displayEmpty
                variant="outlined"
                className={classes.selectField}
                inputProps={selectInputProps}
                value={draft.timeZone}
                onChange={(e) => updateDraft({ timeZone: e.target.value })}
              >
                <MenuItem value="" disabled>
                  --Select Time Zone--
                </MenuItem>
                {TIME_ZONE_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ style: { borderRadius: "18px", width: "680px", maxWidth: "680px" } }}
    >
      {messageInfo && (
        <div className={classes.modalRoot}>
          <div className={classes.modalBody}>
            <div className={classes.cancelRow} onClick={onClose}>
              Cancel <CloseIcon fontSize="small" />
            </div>

            <div className={classes.modalTitle}>Configure IDoc Trigger Point</div>

            <div className={classes.modalInfoBox}>
              <div>
                <div className={classes.infoLabel}>Message</div>
                <div className={classes.infoValue}>{messageInfo.name}</div>
              </div>
              <div>
                <div className={classes.infoLabel}>Build-Version No.</div>
                <div className={classes.infoValue}>
                  {messageInfo.buildNo}
                  {messageInfo.versionNo}
                </div>
              </div>
              <div>
                <div className={classes.infoLabel}>Description</div>
                <div className={classes.infoValue}>{messageInfo.description}</div>
              </div>
              {cmoName != null && (
                <div>
                  <div className={classes.infoLabel}>CMO</div>
                  <div className={classes.infoValue}>{cmoName || "—"}</div>
                </div>
              )}
              {productType != null && (
                <div>
                  <div className={classes.infoLabel}>Product Type</div>
                  <div className={classes.infoValue}>{productType || "—"}</div>
                </div>
              )}
            </div>

            <div className={classes.formField}>
              <div className={classes.fieldLabel}>
                Trigger Point<span className={classes.requiredStar}>*</span>
                {(loadingOptions || loadingExisting) && (
                  <CircularProgress size={14} style={{ marginLeft: 8, verticalAlign: "middle" }} />
                )}
              </div>
              <Select
                displayEmpty
                variant="outlined"
                className={classes.selectField}
                inputProps={selectInputProps}
                value={draft.triggerPoint}
                disabled={loadingOptions || loadingExisting}
                onChange={(e) => {
                  const newLabel = e.target.value;
                  const newTriggerPointID = triggerPoints.find((tp) => tp.triggerPoint === newLabel)
                    ?.collabrixTriggerPointID;
                  setDraft(buildDraftFromDetails(newLabel, newTriggerPointID, originalDetails));
                }}
              >
                <MenuItem value="">--Select Trigger Point--</MenuItem>
                {triggerPoints.map((tp) => (
                  <MenuItem key={tp.collabrixTriggerPointID} value={tp.triggerPoint}>
                    {tp.triggerPoint}
                  </MenuItem>
                ))}
              </Select>
            </div>

            {renderConditionalFields()}

            <Button
              className={classes.modalSaveBtn}
              disabled={!canSaveTriggerConfig(draft) || loadingOptions || loadingExisting || saving}
              style={{
                opacity: canSaveTriggerConfig(draft) && !loadingOptions && !loadingExisting && !saving ? 1 : 0.5,
              }}
              onClick={handleSave}
              startIcon={saving ? <CircularProgress size={16} style={{ color: "#fff" }} /> : null}
            >
              {saving ? "Saving..." : "Save ›"}
            </Button>

            {saveError && (
              <div style={{ color: "#e53935", fontSize: "13px", width: "100%" }}>{saveError}</div>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default ConfigureIDocTriggerPointDialog;
